import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API } from "./api";

async function req(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    let detail = "Une erreur est survenue.";
    let raw = null;
    try { const d = await res.json(); raw = d.detail; detail = typeof d.detail === "string" ? d.detail : detail; } catch (_) {}
    const err = new Error(detail); err.status = res.status; err.detail = raw; throw err;
  }
  return res.status === 204 ? null : res.json();
}

export const authApi = {
  login: (email, password) => req("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  claimSite: (slug, data) => req(`/members/${slug}/claim`, { method: "POST", body: JSON.stringify(data) }),
  registerUser: (data) => req("/auth/register", { method: "POST", body: JSON.stringify(data) }),
  acquereurSummary: (raw_text) => req("/acquereurs/summary", { method: "POST", body: JSON.stringify({ raw_text }) }),
  myAcquereurs: () => req("/acquereurs/me"),
  deleteMyAcquereur: (id) => req(`/acquereurs/${id}`, { method: "DELETE" }),
  renameMyAcquereur: (id, title) => req(`/acquereurs/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),
  setAcquereurStatus: (id, status) => req(`/acquereurs/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  myMatches: () => req("/acquereurs/me/matches"),
  adminAcquereurMatches: (id) => req(`/admin/acquereurs/${id}/matches`),
  getMatchingConfig: () => req("/admin/matching-config"),
  setMatchingConfig: (data) => req("/admin/matching-config", { method: "PUT", body: JSON.stringify(data) }),
  register: (data) => req("/members/register", { method: "POST", body: JSON.stringify(data) }),
  myProfile: () => req("/members/me/profile"),
  me: () => req("/auth/me"),
  logout: () => req("/auth/logout", { method: "POST" }),
  googleSession: (sessionId) => req("/auth/session", { method: "POST", headers: { "X-Session-ID": sessionId } }),
  updateProfile: (data) => req("/members/me/profile", { method: "PATCH", body: JSON.stringify(data) }),
  emailTones: () => req("/email-tones"),
  sendEmailTest: (tone) => req("/member/email-test", { method: "POST", body: JSON.stringify({ tone }) }),
  pauseAccount: (paused) => req("/members/me/pause", { method: "POST", body: JSON.stringify({ paused }) }),
  deleteAccount: () => req("/members/me", { method: "DELETE" }),
  completeProfile: (data) => req("/members/complete-profile", { method: "POST", body: JSON.stringify(data) }),
  myListings: () => req("/member/listings"),
  memberStats: () => req("/member/stats"),
  memberActivity: (type = "") => req(`/member/activity${type ? `?type=${encodeURIComponent(type)}` : ""}`),
  weeklyRecapPreview: (send = false) => req(`/member/weekly-recap/preview${send ? "?send=true" : ""}`),
  prospectionPreview: (acqId) => req(`/member/prospection/preview?acq_id=${encodeURIComponent(acqId)}`),
  prospectionSend: (acqId) => req("/member/prospection/send", { method: "POST", body: JSON.stringify({ acq_id: acqId }) }),
  agentEmails: (range = "30d", status = "") => req(`/member/agent/emails?range=${range}${status ? `&status=${status}` : ""}`),
  agentEmailDetail: (id) => req(`/member/agent/emails/${id}`),
  agentEmailStats: () => req("/member/agent/stats"),
  agentFeed: () => req("/member/agent/feed"),
  emmaWork: () => req("/member/agent/emma-work"),
  whatsappDraft: (acqId) => req(`/member/prospect/${acqId}/whatsapp-draft`, { method: "POST" }),
  boostReport: (slug) => req(`/member/agent/boost-report/${slug}`),
  prospectFiche: (acqId) => req(`/member/prospect/${acqId}`),
  prospectSetSource: (acqId, body) => req(`/member/prospect/${acqId}/source`, { method: "POST", body: JSON.stringify(body) }),
  agentGetSettings: () => req("/member/agent/settings"),
  agentDigestPreview: (send = false) => req(`/member/agent/daily-digest/preview${send ? "?send=true" : ""}`),
  agentSetSettings: (body) => req("/member/agent/settings", { method: "POST", body: JSON.stringify(typeof body === "string" ? { agent_mode: body } : body) }),
  agentProspects: (params = {}) => { const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString(); return req(`/member/agent/prospects${qs ? `?${qs}` : ""}`); },
  prospectSetStatus: (acqId, status) => req(`/member/prospect/${acqId}/status`, { method: "POST", body: JSON.stringify({ status }) }),
  proposeVisite: (acqId, body) => req(`/member/prospect/${acqId}/propose-visite`, { method: "POST", body: JSON.stringify(body) }),
  agentPending: () => req("/member/agent/pending"),
  agentApprove: (id) => req(`/member/agent/emails/${id}/approve`, { method: "POST" }),
  agentReject: (id) => req(`/member/agent/emails/${id}/reject`, { method: "POST" }),
  agentApproveAll: () => req(`/member/agent/pending/approve-all`, { method: "POST" }),
  agentRejectAll: () => req(`/member/agent/pending/reject-all`, { method: "POST" }),
  matchProposalCreate: (data) => req("/member/match-proposals", { method: "POST", body: JSON.stringify(data) }),
  matchProposals: () => req("/member/match-proposals"),
  matchProposalAccept: (id) => req(`/member/match-proposals/${id}/accept`, { method: "POST" }),
  matchProposalDecline: (id) => req(`/member/match-proposals/${id}/decline`, { method: "POST" }),
  boostStatus: (slug) => req(`/member/boost/status/${slug}`),
  boostCheckout: (data) => req("/payments/boost/checkout", { method: "POST", body: JSON.stringify(data) }),
  agentOverview: (range = "all") => req(`/member/admin/agent-overview?range=${encodeURIComponent(range)}`),
  agentLeaderboard: (month = "", criteria = "score") => req(`/member/admin/agent-leaderboard?month=${encodeURIComponent(month)}&criteria=${encodeURIComponent(criteria)}`),
  agentRunRelances: () => req("/member/agent/run-relances", { method: "POST" }),
  mySite: () => req("/member/site"),
  saveSite: (data) => req("/member/site", { method: "PUT", body: JSON.stringify(data) }),
  generateSite: (payload) => req("/member/site/generate", { method: "POST", body: JSON.stringify(payload) }),
  generateNews: (payload) => req("/member/site/news/generate", { method: "POST", body: JSON.stringify(payload) }),
  generateNewsCover: (payload) => req("/member/site/news/generate-cover", { method: "POST", body: JSON.stringify(payload) }),
  socialOverview: () => req("/member/social/overview"),
  saveSocialAccounts: (accounts) => req("/member/social/accounts", { method: "PUT", body: JSON.stringify({ accounts }) }),
  generateReferralAd: (target, tone) => req("/member/social/referral-ad", { method: "POST", body: JSON.stringify({ target, tone }) }),
  generateReferralAdImage: (body) => req("/member/social/referral-ad/image", { method: "POST", body: JSON.stringify(body) }),
  createListing: (data) => req("/member/listings", { method: "POST", body: JSON.stringify(data) }),
  importListingUrl: (url) => req("/member/listings/import-url", { method: "POST", body: JSON.stringify({ url }) }),
  importUrlPreview: (url) => req("/member/listings/import-url/preview", { method: "POST", body: JSON.stringify({ url }) }),
  importUrlConfirm: (data) => req("/member/listings/import-url/confirm", { method: "POST", body: JSON.stringify(data) }),
  refetchPhotos: (slug, body) => req(`/member/listings/${slug}/refetch-photos`, { method: "POST", body: JSON.stringify(body || {}) }),
  importUrlStart: (url) => req("/member/listings/import-url/start", { method: "POST", body: JSON.stringify({ url }) }),
  importJob: (id) => req(`/member/listings/import-job/${id}`),
  importJobActive: () => req("/member/listings/import-job/active"),
  importJobConfirmCity: (id, city) => req(`/member/listings/import-job/${id}/confirm-city`, { method: "POST", body: JSON.stringify(city) }),
  sellerListingStart: (payload) => req("/public/seller-listing/start", { method: "POST", body: JSON.stringify(payload) }),
  visitGenerate: (data) => req("/member/visit/generate", { method: "POST", body: JSON.stringify(data) }),
  classifyRoom: (image_url) => req("/member/classify-room", { method: "POST", body: JSON.stringify({ image_url }) }),
  updateListing: (slug, data) => req(`/member/listings/${slug}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteListing: (slug) => req(`/member/listings/${slug}`, { method: "DELETE" }),
  creditsConfig: () => req("/credits/config"),
  wallet: () => req("/member/wallet"),
  publishListing: (slug, tier) => req(`/member/listings/${slug}/publish`, { method: "POST", body: JSON.stringify({ tier }) }),
  changeTier: (slug, tier) => req(`/member/listings/${slug}/change-tier`, { method: "POST", body: JSON.stringify({ tier }) }),
  checkout: (lookup_key, publish_slug = "", tier = "standard", promo_code = "") => req("/payments/checkout", { method: "POST", body: JSON.stringify({ lookup_key, origin_url: window.location.origin, publish_slug, tier, promo_code }) }),
  paymentStatus: (sessionId) => req(`/payments/status/${sessionId}`),
  generatePreview: (slug, image_url, room = "Salon") => req(`/member/listings/${slug}/preview`, { method: "POST", body: JSON.stringify({ image_url, room }) }),
  stagingBatch: (slug, body) => req(`/member/listings/${slug}/staging-batch`, { method: "POST", body: JSON.stringify(body) }),
  deleteStaging: (slug, id) => req(`/member/listings/${slug}/staging/${id}`, { method: "DELETE" }),
  regenerateStaging: (slug, id) => req(`/member/listings/${slug}/staging/${id}/regenerate`, { method: "POST" }),
  cleanPhoto: (slug, imageUrl) => req(`/member/listings/${slug}/clean-photo`, { method: "POST", body: JSON.stringify({ image_url: imageUrl }) }),
  autoStaging: (slug, body) => req(`/member/listings/${slug}/auto-staging`, { method: "POST", body: JSON.stringify(body) }),
  stagingStyles: () => req("/staging-styles"),
  generateAll: (slug, body) => req(`/member/listings/${slug}/generate-all`, { method: "POST", body: JSON.stringify(body) }),
  generationStatus: (slug) => req(`/member/listings/${slug}/generation-status`),
  shareText: (slug, body) => req(`/listings/${slug}/share-text`, { method: "POST", body: JSON.stringify(body) }),
  shareImage: (slug, body) => req(`/member/listings/${slug}/share-image`, { method: "POST", body: JSON.stringify(body) }),
  trackShare: (slug, network) => req(`/listings/${slug}/share-track`, { method: "POST", body: JSON.stringify({ network }) }),
  metaStatus: () => req("/meta/status"),
  metaLoginUrl: () => req(`/meta/login-url?origin=${encodeURIComponent(window.location.origin)}`),
  metaDisconnect: () => req("/meta/disconnect", { method: "POST" }),
  metaPublish: (body) => req("/meta/publish", { method: "POST", body: JSON.stringify(body) }),
  metaGetSettings: () => req("/meta/settings"),
  metaSetSettings: (body) => req("/meta/settings", { method: "POST", body: JSON.stringify(body) }),
  metaInsights: (slug) => req(`/meta/insights/${slug}`),
  metaDashboard: () => req("/meta/dashboard"),
  sellerLink: (slug) => req(`/member/listings/${slug}/seller-link`, { method: "POST" }),
  memberSellers: () => req("/member/sellers"),
  sellerThread: (slug) => req(`/member/sellers/${slug}/messages`),
  sellerReply: (slug, text) => req(`/member/sellers/${slug}/message`, { method: "POST", body: JSON.stringify({ text }) }),
  acqPortalLink: (id) => req(`/member/acquereurs/${id}/portal-link`, { method: "POST" }),
  listStagingStyles: () => req("/staging-styles"),
  getListing: (slug) => req(`/member/listings/${slug}`),
  redeemPromo: (code) => req("/member/promo/redeem", { method: "POST", body: JSON.stringify({ code }) }),
  adminAcquereurs: () => req("/admin/acquereurs"),
  voiceParseAcq: (transcript) => req("/acquereurs/voice-parse", { method: "POST", body: JSON.stringify({ transcript }) }),
  bulkCreateAcq: (acquereurs, invite) => req("/acquereurs/bulk-create", { method: "POST", body: JSON.stringify({ acquereurs, invite }) }),
  memberAcquereurs: () => req("/member/acquereurs"),
  deleteMemberAcquereur: (id) => req(`/member/acquereurs/${id}`, { method: "DELETE" }),
  matchingAgents: () => req("/member/matching/agents"),
  matchingWhy: (id) => req(`/member/matching/agents/${id}/why`),
  matchingPropose: (body) => req("/member/matching/propose", { method: "POST", body: JSON.stringify(body) }),
  matchingOverview: () => req("/member/matching/overview"),
  matchingExplain: (slug, acq_id) => req("/member/matching/explain", { method: "POST", body: JSON.stringify({ slug, acq_id }) }),
  memberVisits: () => req("/member/visits"),
  createVisit: (b) => req("/member/visits", { method: "POST", body: JSON.stringify(b) }),
  updateVisit: (id, b) => req(`/member/visits/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  deleteVisit: (id) => req(`/member/visits/${id}`, { method: "DELETE" }),
  generateVisitReport: (id) => req(`/member/visits/${id}/generate-report`, { method: "POST", body: JSON.stringify({}) }),
  opportunities: () => req("/member/opportunities"),
  generateListingContent: (slug) => req(`/member/listings/${slug}/generate-content`, { method: "POST", body: JSON.stringify({}) }),
  crmHome: () => req("/member/crm-home"),
  socialConnections: () => req("/member/social-connections"),
  connectSocial: (network, handle, url) => req(`/member/social-connections/${network}`, { method: "POST", body: JSON.stringify({ handle, url }) }),
  disconnectSocial: (network) => req(`/member/social-connections/${network}`, { method: "DELETE" }),
  commissions: () => req("/member/commissions"),
  adminCommissions: () => req("/admin/commissions"),
  adminPromos: () => req("/admin/promo-codes"),
  adminCreatePromo: (data) => req("/admin/promo-codes", { method: "POST", body: JSON.stringify(data) }),
  adminDeletePromo: (code) => req(`/admin/promo-codes/${code}`, { method: "DELETE" }),
  adminGetCreditConfig: () => req("/admin/credit-config"),
  adminUpdateCreditConfig: (data) => req("/admin/credit-config", { method: "PUT", body: JSON.stringify(data) }),
  netSettings: () => req("/network/settings"),
  netSaveSettings: (data) => req("/network/settings", { method: "PUT", body: JSON.stringify(data) }),
  netDirectory: (q = "") => req(`/network/directory${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  netPartnerships: () => req("/network/partnerships"),
  netRequest: (target_id) => req("/network/partnerships", { method: "POST", body: JSON.stringify({ target_id }) }),
  netAccept: (id) => req(`/network/partnerships/${id}/accept`, { method: "POST" }),
  netReject: (id) => req(`/network/partnerships/${id}/reject`, { method: "POST" }),
  netTerminate: (id) => req(`/network/partnerships/${id}`, { method: "DELETE" }),
  netToggleFav: (member_id) => req(`/network/favorites/${member_id}`, { method: "POST" }),
  netInvite: (emails, message) => req("/network/invite", { method: "POST", body: JSON.stringify({ emails, message }) }),
  netInvitations: () => req("/network/invitations"),
  netRemind: (id) => req(`/network/invite/${id}/remind`, { method: "POST" }),
  netInviteParse: async (fileObj) => {
    const fd = new FormData();
    fd.append("file", fileObj);
    const res = await fetch(`${API}/network/invite/parse`, { method: "POST", credentials: "include", body: fd });
    if (!res.ok) { let d = "Erreur lors de la lecture du fichier."; try { const j = await res.json(); d = typeof j.detail === "string" ? j.detail : d; } catch (_) {} const e = new Error(d); e.status = res.status; throw e; }
    return res.json();
  },
  netSharedListings: () => req("/network/shared-listings"),
  subStatus: () => req("/member/subscription"),
  subCheckout: () => req("/member/subscription/checkout", { method: "POST", body: JSON.stringify({ origin_url: window.location.origin }) }),
  subCancel: () => req("/member/subscription/cancel", { method: "POST" }),
  publishSite: (published) => req("/member/site/publish", { method: "POST", body: JSON.stringify({ published }) }),
  setDomain: (domain) => req("/member/site/domain", { method: "PUT", body: JSON.stringify({ domain }) }),
  verifyDomain: () => req("/member/site/domain/verify", { method: "POST" }),
  netRecommendations: () => req("/network/recommendations"),
  netGroups: () => req("/network/groups"),
  netSaveGroups: (groups) => req("/network/groups", { method: "PUT", body: JSON.stringify({ groups }) }),
  netAssignGroups: (member_id, groups) => req(`/network/partners/${member_id}/groups`, { method: "POST", body: JSON.stringify({ groups }) }),
  netStats: () => req("/network/stats"),
  netApports: () => req("/network/apports"),
  netUpdateApport: (id, status) => req(`/network/apports/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  netApportPayout: (id, payout_status) => req(`/network/apports/${id}`, { method: "PATCH", body: JSON.stringify({ payout_status }) }),
  netApportsAnalytics: () => req("/network/apports/analytics"),
  auditListings: () => req("/network/audit"),
  auditAllListings: () => req("/network/audit/all"),
  auditGenerate: (slug, body) => req(`/audit/${slug}/generate`, { method: "POST", body: JSON.stringify(body || {}) }),
  auditRegenHighlights: (slug) => req(`/audit/${slug}/highlights`, { method: "POST" }),
  auditCompleteAll: (body) => req("/audit/complete-all", { method: "POST", body: JSON.stringify(body || {}) }),
  auditJob: (id) => req(`/audit/job/${id}`),
  auditCities: (scope = "mine") => req(`/audit/cities?scope=${scope}`),
  auditSetCity: (slug, body) => req(`/audit/${slug}/city`, { method: "POST", body: JSON.stringify(body || {}) }),
  geoValidate: (q, postal = "", department = "") => req(`/geo/validate?q=${encodeURIComponent(q)}&postal=${encodeURIComponent(postal)}&department=${encodeURIComponent(department)}`),
  geoPrecheck: (body) => req("/geo/precheck", { method: "POST", body: JSON.stringify(body) }),
  netComandats: () => req("/network/comandats"),
  netCreateComandat: (data) => req("/network/comandats", { method: "POST", body: JSON.stringify(data) }),
  netUpdateComandat: (id, data) => req(`/network/comandats/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  netMarkComandatSold: (id, data) => req(`/network/comandats/${id}/mark-sold`, { method: "POST", body: JSON.stringify(data) }),
  netCancelComandat: (id) => req(`/network/comandats/${id}`, { method: "DELETE" }),
  offersMine: () => req("/offers/mine/list"),
  offerAdvisorDetail: (id) => req(`/offers/${id}/advisor`),
  offerAdvisorAction: (id, payload) => req(`/offers/${id}/advisor-action`, { method: "POST", body: JSON.stringify(payload) }),
  offersAdminAll: () => req("/offers/admin/all"),
  docusignAvailable: () => req("/docusign/available"),
  docusignSend: (id, kind, payload) => req(`/offers/${id}/docusign/${kind}/send`, { method: "POST", body: JSON.stringify(payload || {}) }),
  docusignStatus: (id, kind) => req(`/offers/${id}/docusign/${kind}/status`),
};

export async function uploadMemberFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/public/upload`, { method: "POST", body: fd, credentials: "include" });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json())?.detail || ""; } catch (_) {}
    throw new Error(detail || (res.status === 413 ? "Fichier trop volumineux" : "Upload impossible"));
  }
  return res.json(); // { url, path }
}

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth

  const checkAuth = useCallback(async () => {
    try { setUser(await authApi.me()); } catch (_) { setUser(false); }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback will handle
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => { const u = await authApi.login(email, password); setUser(u); return u; };
  const logout = async () => { try { await authApi.logout(); } catch (_) {} setUser(false); };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
